import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, CheckCircle2, GraduationCap, Briefcase,
  Building2, Clock, Globe2, MapPin, Sparkles, Cpu, Plane, BadgeCheck,
  TrendingUp, MessageCircle, HelpCircle, AlertCircle, Coins, Trophy,
  Star, FileText, ChevronRight,
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../../components/ui/accordion';
import { usePageMeta, useServiceSchema, useFAQSchema } from '../../hooks/useSEO';
import BreadcrumbSchema from '../../components/seo/BreadcrumbSchema';

/* ─── Static content (kept top-level so the component stays slim) ─── */

const HERO_PROPS = [
  { icon: Coins,    title: 'Tax-Free Earnings',      copy: '0% personal income tax on local salaries — your Dubai pay-cheque is what you take home.' },
  { icon: Cpu,      title: 'D33 Job Creation Engine', copy: 'AED 32 trillion economic agenda is doubling demand for AI, Fintech, Logistics & Sustainability talent.' },
  { icon: BadgeCheck, title: 'Student → Resident',   copy: 'Golden Visa for outstanding graduates + 1-year Job Seeker Visa make the post-study transition realistic.' },
];

const HUB_LINKS = [
  { title: 'Cost of Living & Tuition',     href: '/study-abroad/dubai/cost-of-living-tuition/',           icon: Coins,       desc: 'AED tuition + area-wise monthly living cost tables.' },
  { title: 'Student Work Rights',          href: '/study-abroad/dubai/student-work-rights/',              icon: Briefcase,   desc: '20-hour work permit, DIFC / Internet City internships.' },
  { title: 'Golden Visa & PR Pathways',    href: '/study-abroad/dubai/golden-visa-post-study-pathways/',  icon: BadgeCheck,  desc: '10-year Golden Visa + Job Seeker Visa for graduates.' },
  { title: 'Top Dubai Universities',       href: '/study-abroad/dubai/universities/',                     icon: GraduationCap, desc: 'DIAC & Knowledge Park campuses, rankings, fees.' },
  { title: 'Best Courses for Residency',   href: '/study-abroad/dubai/top-courses-for-residency/',        icon: TrendingUp,  desc: 'AI, Fintech, DEWA, Luxury Hospitality — 2026 shortages.' },
];

const TRUST_LINKS = [
  { title: 'About ZAFCO Dubai',         href: '/study-abroad/dubai/about-zafco/',           icon: Sparkles,    desc: 'Career Migration Ecosystem — 3-pillar model, 10,000+ placements.' },
  { title: 'Why Choose ZAFCO',          href: '/study-abroad/dubai/why-choose-zafco/',      icon: BadgeCheck,  desc: 'MEA-approved licensure, Migration Intelligence, 90-day post-landing support.' },
  { title: 'University Partnerships',   href: '/study-abroad/dubai/university-partnerships/', icon: Globe2,    desc: '500+ universities — strategic vetting, priority CAS/COE, exclusive scholarships.' },
  { title: 'Events & Webinars',         href: '/study-abroad/dubai/events-webinars/',       icon: MessageCircle, desc: 'Alumni Power Panels, University Masterclasses, PR Skill Clinics.' },
  { title: 'Visa Success Stories',      href: '/study-abroad/dubai/visa-success-stories/',  icon: Trophy,      desc: '98%+ visa approval — vetting pipeline, verified testimonials, 2025 stats.' },
];

const FEATURED_UNIS = [
  { name: 'Heriot-Watt University Dubai',  cluster: 'DIAC',          slug: 'heriot-watt-university-dubai-campus',   strength: 'Engineering · Petroleum · AI' },
  { name: 'University of Wollongong Dubai (UOWD)', cluster: 'Knowledge Park', slug: 'university-of-wollongong-dubai', strength: 'Computer Science · Business' },
  { name: 'Middlesex University Dubai',    cluster: 'Knowledge Park',  slug: 'middlesex-university-dubai-campus',     strength: 'Media · Business · Psychology' },
  { name: 'American University in Dubai',  cluster: 'Al Sufouh',        slug: 'american-university-in-dubai',          strength: 'Architecture · MBA · Engineering' },
  { name: 'University of Birmingham Dubai',cluster: 'DIAC',             slug: 'university-of-birmingham-dubai',        strength: 'Russell Group · Engineering · CS' },
];

const TUITION_UG = [
  { course: 'Business & Management', low: 75000, high: 95000 },
  { course: 'Engineering / CS',      low: 90000, high: 120000 },
  { course: 'Media & Design',        low: 80000, high: 110000 },
  { course: 'Hospitality',           low: 75000, high: 100000 },
];

const TUITION_PG = [
  { course: 'MBA (1-Year)',                 low: 95000,  high: 120000 },
  { course: 'MSc Data Science / AI',        low: 85000,  high: 115000 },
  { course: 'MSc Fintech / Finance',        low: 90000,  high: 120000 },
  { course: 'MSc Luxury / Hospitality Mgmt', low: 85000, high: 110000 },
];

const LIVING_COST = [
  { area: 'Academic City / IAC',     rent: '1,800 – 2,500', food: '900 – 1,200', transport: '350 – 500',  total: '3,500 – 4,500',  badge: 'Student-budget' },
  { area: 'Al Barsha / Tecom',       rent: '2,200 – 3,200', food: '1,000 – 1,400', transport: '500 – 700', total: '4,000 – 5,500', badge: 'Balanced' },
  { area: 'Dubai Marina / JLT',      rent: '3,500 – 5,000', food: '1,200 – 1,600', transport: '600 – 900', total: '5,500 – 7,500', badge: 'Premium' },
];

const RESIDENCY_COURSES = [
  { field: 'AI & Robotics',         employers: 'G42, Microsoft, IBM, Presight', salary: 'AED 18k–30k/mo', pathway: 'Golden Visa (Specialist Talent)' },
  { field: 'Fintech & Blockchain',  employers: 'DIFC, ADGM, Mashreq, Tabby',    salary: 'AED 16k–28k/mo', pathway: '5-yr Green Visa → Golden Visa' },
  { field: 'Sustainable Energy',    employers: 'DEWA, Masdar, ENOC, Siemens Energy', salary: 'AED 14k–24k/mo', pathway: '5-yr Green Visa → Golden Visa' },
  { field: 'Luxury Hospitality',    employers: 'Emaar, Jumeirah, Atlantis, Four Seasons', salary: 'AED 12k–22k/mo', pathway: '2-yr work + sponsored visa' },
];

const FAQS = [
  { q: 'Is Dubai income really tax-free for graduates?', a: 'Yes. The UAE imposes 0% personal income tax on residents\' employment income. The 9% corporate tax (from 2023) applies to business profits above AED 375k, not employee salaries. Your Dubai net pay = gross pay.' },
  { q: 'How many hours can international students work in Dubai?', a: 'Students on a valid UAE student visa can work up to 20 hours/week during semester and full-time during vacations, with a work permit issued by the Ministry of Human Resources & Emiratisation (MoHRE). Free-zone universities (DIAC, Knowledge Park) coordinate the paperwork.' },
  { q: 'What GPA is required for the Dubai Golden Visa after graduation?', a: 'Outstanding-graduate Golden Visas typically require a GPA of 3.5+ (out of 4.0) or equivalent first-class honours, plus graduation from a recognised UAE-accredited or top-100 global university. The visa is valid for 10 years and is renewable.' },
  { q: 'What is the average tuition fee in Dubai (AED)?', a: 'Undergraduate: AED 75,000 – 120,000/year. Postgraduate: AED 85,000 – 120,000/year. STEM and MBA programmes sit at the higher end; Business and Media at the lower end. Pricing is fixed annually by KHDA.' },
  { q: 'Which areas in Dubai are most affordable for students?', a: 'Dubai International Academic City (AED 3,500–4,500/month all-in) and Al Barsha/Tecom (AED 4,000–5,500) are the most student-friendly clusters. Marina/JLT (AED 5,500–7,500) are premium but offer DIFC/Media City internship proximity.' },
  { q: 'Is the UAE Job Seeker Visa available to graduates?', a: 'Yes. Skilled graduates of top 500 global universities or UAE-accredited universities can apply for a 60 / 90 / 120-day Job Seeker entry permit (sponsor-free), letting them attend interviews and finalise an offer before switching to a work residency.' },
  { q: 'Are Dubai degrees recognised by Indian employers (UGC/AICTE)?', a: 'Degrees from KHDA-accredited universities operating in DIAC/Knowledge Park are recognised by the Association of Indian Universities (AIU) and most Indian employers. Always confirm the specific programme is KHDA + home-university accredited.' },
  { q: 'What is the D33 Dubai Economic Agenda?', a: 'D33 is the 2033 economic agenda set by Sheikh Mohammed targeting AED 32 trillion in cumulative economic output, 400+ city partnerships, doubling FDI, and 30+ new unicorns — fuelling massive demand for skilled graduates in AI, fintech, logistics, sustainability and the digital economy.' },
];

const fmtAED = (n) => `AED ${n.toLocaleString('en-AE')}`;

/* ─── Component ─── */

export default function DubaiHubPage() {
  usePageMeta(
    'Study in Dubai from India 2026 — D33 Hub, Golden Visa & Tax-Free Careers',
    'High-authority Dubai/UAE study guide for Indian students: D33 economic agenda, tax-free earnings, Golden Visa, DIAC & Knowledge Park universities, AED tuition + living-cost tables, and 2026 skill-shortage courses.'
  );

  useServiceSchema({
    name: 'Dubai Study Abroad Counselling',
    serviceType: 'Education Counselling',
    areaServed: 'India',
    description: 'End-to-end admissions, KHDA university selection, student-visa documentation, and post-study Golden Visa / Job Seeker Visa planning for Indian students applying to Dubai/UAE universities.',
  });

  useFAQSchema(FAQS);

  /* Course JSON-LD for flagship Dubai programmes — injected as a single script tag. */
  useEffect(() => {
    const tag = 'dubai-hub-course-schema';
    const old = document.getElementById(tag);
    if (old) old.remove();

    const courses = [
      {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: 'MSc Artificial Intelligence (Dubai)',
        description: 'Industry-aligned MSc in AI for Dubai\'s G42 / Microsoft / Presight ecosystem under the D33 agenda.',
        provider: { '@type': 'CollegeOrUniversity', name: 'Heriot-Watt University Dubai', sameAs: 'https://www.hw.ac.uk/dubai/' },
        educationalCredentialAwarded: 'Master of Science',
        offers: { '@type': 'Offer', priceCurrency: 'AED', price: '95000', category: 'Tuition (per year)' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: 'MBA (Dubai)',
        description: 'One-year MBA tailored to the DIFC / fintech / luxury-hospitality job market in Dubai.',
        provider: { '@type': 'CollegeOrUniversity', name: 'American University in Dubai', sameAs: 'https://www.aud.edu/' },
        educationalCredentialAwarded: 'Master of Business Administration',
        offers: { '@type': 'Offer', priceCurrency: 'AED', price: '110000', category: 'Tuition (total programme)' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: 'BEng Mechanical / Petroleum Engineering (Dubai)',
        description: 'Engineering undergraduate degree feeding ENOC, ADNOC, Siemens Energy and DEWA pipelines.',
        provider: { '@type': 'CollegeOrUniversity', name: 'Heriot-Watt University Dubai', sameAs: 'https://www.hw.ac.uk/dubai/' },
        educationalCredentialAwarded: 'Bachelor of Engineering',
        offers: { '@type': 'Offer', priceCurrency: 'AED', price: '90000', category: 'Tuition (per year)' },
      },
    ];

    const s = document.createElement('script');
    s.id = tag;
    s.type = 'application/ld+json';
    s.text = JSON.stringify(courses);
    document.head.appendChild(s);
    return () => { document.getElementById(tag)?.remove(); };
  }, []);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-hub-page">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://zafco.in/' },
          { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
          { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        ]}
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F1B2C] via-[#0B1322] to-[#0F1B2C]">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1920&q=80&fm=webp"
            alt="Burj Khalifa and Dubai International Financial Centre skyline at twilight"
            className="w-full h-full object-cover"
            width="1920" height="1080" loading="eager" fetchPriority="high" decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B2C]/95 via-[#0F1B2C]/70 to-transparent" />
        </div>
        <div className="relative container max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 md:py-24">
          <Link to="/study-abroad/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Study Abroad
          </Link>
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">D33 Agenda · 2026 Cohort</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white font-syne leading-tight max-w-4xl">
            Study in Dubai from India —<br className="hidden sm:inline" /> <span className="text-[#D4AF37]">High-Authority Hub</span> (2026)
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed">
            Dubai is no longer a transit city — it&apos;s a long-term career destination. The <strong className="text-white">Dubai Economic Agenda D33</strong> is targeting <strong className="text-[#D4AF37]">AED 32 trillion</strong> in cumulative output by 2033, doubling demand for graduates in AI, Fintech, Logistics, Sustainability & Luxury Hospitality. Tax-free earnings, Golden Visa pathways, and KHDA-accredited campuses make it the smartest 2026 study destination for Indian students.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {HERO_PROPS.map((p) => (
              <div key={p.title} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/40 transition-colors">
                <p.icon className="w-7 h-7 text-[#D4AF37] mb-3" aria-hidden="true" />
                <h3 className="text-white font-bold font-syne mb-1.5">{p.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a
              href="https://wa.me/919990533327?text=Hello%20Zafco,%20I%20want%20to%20book%20Free%20Dubai%20Counselling%20for%202026."
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-amber-400 text-[#0F1B2C] font-bold px-6 py-3.5 rounded-full transition-colors"
              data-testid="dubai-hub-hero-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" /> Book Free Dubai Counselling
            </a>
            <Link
              to="/study-abroad/book-counselling/?source=dubai-hub-2026"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-6 py-3.5 rounded-full transition-colors"
              data-testid="dubai-hub-hero-counselling-cta"
            >
              <FileText className="w-4 h-4" /> Get Personalised Roadmap
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ACADEMIC INFRASTRUCTURE ============ */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dubai-academic-infra">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">Academic Infrastructure</p>
            <h2 className="text-2xl md:text-4xl font-bold font-syne text-slate-900 mb-3">DIAC &amp; Knowledge Park — Two Engines, One City</h2>
            <p className="text-slate-600 leading-relaxed">Dubai\u2019s international universities are concentrated in two free-zone clusters, both regulated by <strong>KHDA</strong> (Knowledge &amp; Human Development Authority). Programs from these campuses carry the same accreditation as their UK / US / Australian home institutions.</p>
          </div>

          <div className="mt-9 grid md:grid-cols-2 gap-5">
            <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors" data-testid="cluster-diac">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#D4AF37]" /></div>
                <h3 className="text-xl font-bold font-syne">Dubai International Academic City (DIAC)</h3>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">The world&apos;s largest free zone dedicated to higher education — 27+ universities, 25,000+ students, purpose-built campuses. Strong in <strong>Engineering, IT, Business and Medicine</strong>.</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Heriot-Watt, Birmingham, Bradford, Murdoch, Manipal campuses</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> 5,000+ student-accommodation beds inside the free zone</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Direct Metro link to Dubai Mall, DIFC and Marina</li>
              </ul>
            </article>

            <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors" data-testid="cluster-knowledge-park">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-[#D4AF37]" /></div>
                <h3 className="text-xl font-bold font-syne">Dubai Knowledge Park (DKP)</h3>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">The original training &amp; HR cluster — premium business schools, professional certification, and creative-industry programmes co-located with Media &amp; Internet City employers.</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Middlesex, Wollongong, Hult, S P Jain, Manipal Aviation</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Adjacent to Media City, Internet City, Studio City employers</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Strong MBA, Media, Hospitality and Aviation programmes</li>
              </ul>
            </article>
          </div>

          {/* Eligibility */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="bg-white border-l-4 border-[#D4AF37] rounded-r-2xl p-5">
              <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Undergraduate Eligibility</p>
              <p className="text-slate-700 leading-relaxed text-sm">12th grade <strong>60% – 70%</strong> (PCM for engineering · Maths/Commerce for business) · IELTS 6.0 / TOEFL 70 · Some campuses accept Duolingo 100+ · Sponsor letter from parent.</p>
            </div>
            <div className="bg-white border-l-4 border-emerald-600 rounded-r-2xl p-5">
              <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-1">Postgraduate Eligibility</p>
              <p className="text-slate-700 leading-relaxed text-sm">Bachelor&apos;s <strong>50% – 60%</strong> in relevant discipline · IELTS 6.5 / TOEFL 88 · 2+ years work experience for MBA · Statement of Purpose &amp; 2 LORs.</p>
            </div>
          </div>

          {/* Featured campuses */}
          <div className="mt-10">
            <h3 className="text-xl md:text-2xl font-bold font-syne mb-5">Featured Campuses</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FEATURED_UNIS.map((u) => (
                <Link
                  key={u.slug}
                  to={`/study-abroad/universities/dubai/${u.slug}/`}
                  className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#D4AF37] hover:shadow-lg transition-all block"
                  data-testid={`featured-uni-${u.slug}`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37] mb-1.5">{u.cluster}</p>
                  <h4 className="font-bold font-syne text-slate-900 group-hover:text-[#0F1B2C] leading-tight">{u.name}</h4>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{u.strength}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F1B2C] mt-3 group-hover:translate-x-0.5 transition-transform">
                    Programme details <ArrowUpRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINANCIAL TABLES (AED) ============ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dubai-financial-tables">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">Tuition &amp; Living Cost</p>
          <h2 className="text-2xl md:text-4xl font-bold font-syne text-slate-900 mb-3">Real Numbers, in AED</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mb-9">All figures are 2026 KHDA-published ranges sourced from official tuition catalogues. Pair them with the area-wise living cost grid below to build a realistic annual budget.</p>

          {/* Tuition tables */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
              <div className="bg-[#0F1B2C] text-white px-5 py-3 font-bold font-syne text-sm uppercase tracking-wider">Undergraduate Tuition (per year)</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr><th className="text-left px-5 py-2.5 font-semibold">Course</th><th className="text-right px-5 py-2.5 font-semibold">Low (AED)</th><th className="text-right px-5 py-2.5 font-semibold">High (AED)</th></tr>
                </thead>
                <tbody>
                  {TUITION_UG.map((r) => (
                    <tr key={r.course} className="border-t border-slate-200">
                      <td className="px-5 py-2.5 font-medium text-slate-800">{r.course}</td>
                      <td className="px-5 py-2.5 text-right font-mono text-slate-700">{fmtAED(r.low)}</td>
                      <td className="px-5 py-2.5 text-right font-mono font-semibold text-[#0F1B2C]">{fmtAED(r.high)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
              <div className="bg-[#0F1B2C] text-white px-5 py-3 font-bold font-syne text-sm uppercase tracking-wider">Postgraduate Tuition (per year)</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr><th className="text-left px-5 py-2.5 font-semibold">Course</th><th className="text-right px-5 py-2.5 font-semibold">Low (AED)</th><th className="text-right px-5 py-2.5 font-semibold">High (AED)</th></tr>
                </thead>
                <tbody>
                  {TUITION_PG.map((r) => (
                    <tr key={r.course} className="border-t border-slate-200">
                      <td className="px-5 py-2.5 font-medium text-slate-800">{r.course}</td>
                      <td className="px-5 py-2.5 text-right font-mono text-slate-700">{fmtAED(r.low)}</td>
                      <td className="px-5 py-2.5 text-right font-mono font-semibold text-[#0F1B2C]">{fmtAED(r.high)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Living cost */}
          <div className="mt-10">
            <h3 className="text-xl md:text-2xl font-bold font-syne text-slate-900 mb-4">Living Cost Comparison — Monthly (AED)</h3>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#0F1B2C] text-white">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Area</th>
                    <th className="text-right px-5 py-3 font-semibold">Rent (Shared)</th>
                    <th className="text-right px-5 py-3 font-semibold">Food</th>
                    <th className="text-right px-5 py-3 font-semibold">Transport</th>
                    <th className="text-right px-5 py-3 font-semibold">Total / month</th>
                  </tr>
                </thead>
                <tbody>
                  {LIVING_COST.map((row) => (
                    <tr key={row.area} className="border-t border-slate-200 even:bg-white">
                      <td className="px-5 py-3 font-medium text-slate-800">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                          {row.area}
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded-full px-2 py-0.5 ml-1">{row.badge}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700">{row.rent}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700">{row.food}</td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700">{row.transport}</td>
                      <td className="px-5 py-3 text-right font-mono font-bold text-[#0F1B2C]">AED {row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">All figures in AED. INR conversion ≈ ₹22.6 / AED (Feb 2026 reference). 1 AED ≈ 1 currency-of-record under KHDA pricing.</p>
          </div>
        </div>
      </section>

      {/* ============ WORK RIGHTS & FREE ZONES ============ */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white" data-testid="dubai-work-rights">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">Work Rights &amp; Internships</p>
              <h2 className="text-2xl md:text-4xl font-bold font-syne mb-3">20 Hours / Week — Inside the World&apos;s Best Free Zones</h2>
              <p className="text-slate-300 leading-relaxed mb-5">UAE student-visa holders can work <strong className="text-white">up to 20 hours per week</strong> during semester (full-time during vacations) under a work permit issued by the <strong className="text-white">Ministry of Human Resources &amp; Emiratisation (MoHRE)</strong>. Free-zone universities coordinate the paperwork — typical permit issuance is 2–3 weeks.</p>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3"><Briefcase className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span><strong className="text-white">DIFC (Finance):</strong> 250+ regulated financial firms — fintech and analytics internships 15-min Metro from DIAC.</span></li>
                <li className="flex gap-3"><Cpu className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Dubai Internet City (Tech):</strong> Microsoft, Google, Oracle, IBM, Meta MENA HQs — AI / cloud / SaaS internships.</span></li>
                <li className="flex gap-3"><Globe2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Dubai Media City &amp; Studio City:</strong> Sky News Arabia, MBC, CNN, BBC, OSN — journalism, production, social-media internships.</span></li>
                <li className="flex gap-3"><Plane className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span><strong className="text-white">DMCC &amp; JAFZA (Logistics):</strong> 22,000+ commodities &amp; trade firms — supply-chain &amp; trading-floor internships.</span></li>
              </ul>
              <Link to="/study-abroad/dubai/student-work-rights/" className="inline-flex items-center gap-2 mt-6 text-[#D4AF37] hover:text-amber-300 font-semibold" data-testid="link-work-rights-detail">
                Full work-permit guide <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 md:p-7">
              <h3 className="font-bold font-syne mb-4 text-[#D4AF37] text-sm uppercase tracking-widest">2026 Average Student Pay</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10"><th className="text-left py-2 font-semibold text-slate-200">Role</th><th className="text-right py-2 font-semibold text-slate-200">Hourly (AED)</th></tr></thead>
                <tbody className="text-slate-300">
                  {[
                    ['Tech / SaaS intern (Internet City)', '40 – 75'],
                    ['DIFC analyst intern (Fintech)',     '50 – 90'],
                    ['Hospitality F&B (Marina hotels)',   '25 – 40'],
                    ['Retail / Mall (Dubai Mall, MoE)',   '22 – 35'],
                    ['Media / content production',        '35 – 60'],
                  ].map(([role, rate]) => (
                    <tr key={role} className="border-b border-white/5">
                      <td className="py-2.5">{role}</td>
                      <td className="py-2.5 text-right font-mono font-semibold text-white">{rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">A motivated student typically nets <strong className="text-white">AED 2,500 – 5,500 / month</strong> — enough to cover housing &amp; food in DIAC or Al Barsha.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POST-STUDY & RESIDENCY ============ */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dubai-residency">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">Post-Study &amp; Residency</p>
          <h2 className="text-2xl md:text-4xl font-bold font-syne text-slate-900 mb-3">The Competitive Edge — Student → Resident</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mb-9">Dubai treats top graduates as long-term economic assets under the D33 talent agenda. Two formal pathways turn an Indian student-visa into a multi-year residency — without leaving the country.</p>

          <div className="grid md:grid-cols-2 gap-6">
            <article className="bg-gradient-to-br from-amber-50 to-white border border-[#D4AF37]/40 rounded-2xl p-6 md:p-7" data-testid="card-golden-visa">
              <div className="flex items-center gap-3 mb-3">
                <Trophy className="w-7 h-7 text-[#D4AF37]" />
                <h3 className="text-xl font-bold font-syne">Golden Visa — 10 Years</h3>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">Renewable 10-year residency for <strong>outstanding graduates</strong> (GPA 3.5+ / first-class) from UAE-accredited or top-100 global universities. No sponsor required, dependants included, work in any sector.</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Self-sponsor — no employer tying you down</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Spouse &amp; children sponsorable</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Path to citizenship via long-term residence</li>
              </ul>
              <Link to="/study-abroad/dubai/golden-visa-post-study-pathways/" className="inline-flex items-center gap-2 mt-5 text-[#0F1B2C] hover:text-[#D4AF37] font-semibold" data-testid="link-golden-visa-detail">
                Eligibility &amp; how to apply <ArrowRight className="w-4 h-4" />
              </Link>
            </article>

            <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7" data-testid="card-jobseeker-visa">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-7 h-7 text-emerald-600" />
                <h3 className="text-xl font-bold font-syne">Job Seeker Visa — 60 / 90 / 120 days</h3>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">Sponsor-free entry permit for skilled graduates — attend interviews, finalise an offer, then convert to a work residency without exiting the UAE.</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Available to top 500 / UAE-accredited graduates</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Choose duration based on your job-search runway</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Convert in-country once offer letter is signed</li>
              </ul>
              <Link to="/study-abroad/dubai/golden-visa-post-study-pathways/" className="inline-flex items-center gap-2 mt-5 text-[#0F1B2C] hover:text-[#D4AF37] font-semibold">
                Compare with Green Visa <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* ============ 2026 SKILL SHORTAGES ============ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dubai-skill-shortages">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">2026 Skill Shortages</p>
          <h2 className="text-2xl md:text-4xl font-bold font-syne text-slate-900 mb-3">Best Courses for Residency</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mb-7">Choose a degree that maps to a D33 priority sector — every field below is on the UAE&apos;s 2026 talent shortage list with explicit Golden Visa or Green Visa pathways.</p>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#0F1B2C] text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Field</th>
                  <th className="text-left px-5 py-3 font-semibold">Top Employers</th>
                  <th className="text-right px-5 py-3 font-semibold">2026 Salary</th>
                  <th className="text-left px-5 py-3 font-semibold">Residency Pathway</th>
                </tr>
              </thead>
              <tbody>
                {RESIDENCY_COURSES.map((row) => (
                  <tr key={row.field} className="border-t border-slate-200 even:bg-white">
                    <td className="px-5 py-3 font-bold text-[#0F1B2C]">{row.field}</td>
                    <td className="px-5 py-3 text-slate-700">{row.employers}</td>
                    <td className="px-5 py-3 text-right font-mono font-semibold text-[#0F1B2C]">{row.salary}</td>
                    <td className="px-5 py-3 text-slate-700">
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full px-2.5 py-1">
                        <BadgeCheck className="w-3.5 h-3.5" /> {row.pathway}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Link to="/study-abroad/dubai/top-courses-for-residency/" className="inline-flex items-center gap-2 mt-5 text-[#0F1B2C] hover:text-[#D4AF37] font-semibold" data-testid="link-top-courses-detail">
            Full sector deep-dive <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ============ HUB NAVIGATOR ============ */}
      <section className="py-14 md:py-20 bg-slate-100" data-testid="dubai-hub-navigator">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne text-slate-900 mb-7">Dive deeper into Dubai 2026</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HUB_LINKS.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] hover:shadow-md transition-all"
                data-testid={`hub-link-${l.href.split('/').filter(Boolean).pop()}`}
              >
                <l.icon className="w-7 h-7 text-[#D4AF37] mb-3" aria-hidden="true" />
                <h3 className="font-bold font-syne text-slate-900 group-hover:text-[#0F1B2C] mb-1.5">{l.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{l.desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#0F1B2C] mt-3 group-hover:translate-x-1 transition-transform">
                  Read guide <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>

          {/* ============ TRUST QUICK LINKS — About / Partnerships / Events / Success ============ */}
          <div className="mt-12 pt-12 border-t border-slate-200" data-testid="dubai-trust-quick-links">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Quick Links · About ZAFCO Dubai</p>
                <h3 className="text-xl md:text-2xl font-bold font-syne text-slate-900">Trust resources — vet us before you sign</h3>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TRUST_LINKS.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#D4AF37] hover:shadow-md transition-all"
                  data-testid={`trust-link-${l.href.split('/').filter(Boolean).pop()}`}
                >
                  <l.icon className="w-6 h-6 text-[#D4AF37] mb-2" aria-hidden="true" />
                  <h4 className="font-bold font-syne text-slate-900 group-hover:text-[#0F1B2C] mb-1">{l.title}</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">{l.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dubai-faq">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center gap-3 mb-3">
            <HelpCircle className="w-7 h-7 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-bold font-syne text-slate-900">Dubai 2026 — FAQs</h2>
          </div>
          <p className="text-slate-600 mb-7 leading-relaxed">Schema-marked answers to the 8 most-asked questions on costs, visas, work rights and residency.</p>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="bg-slate-50 border border-slate-200 rounded-xl px-5">
                <AccordionTrigger className="font-bold font-syne text-left text-slate-900 hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#0F1B2C] via-[#0B1322] to-[#0F1B2C] text-white" data-testid="dubai-final-cta">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <Star className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4 max-w-3xl mx-auto">Ready to lock-in your 2026 Dubai admission?</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">Our KHDA-savvy counsellors will match your profile to the right campus, lock the Sep-2026 / Jan-2027 intake, and map a clean Golden-Visa pathway after graduation.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/919990533327?text=Hello%20Zafco,%20I%20want%20Free%20Dubai%20Counselling%20for%202026."
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-amber-400 text-[#0F1B2C] font-bold px-7 py-3.5 rounded-full transition-colors"
              data-testid="dubai-final-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp +91-9990533327
            </a>
            <Link
              to="/study-abroad/book-counselling/?source=dubai-hub-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="dubai-final-counselling-cta"
            >
              <FileText className="w-4 h-4" /> Book Free Dubai Counselling
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-5">MEA RC: B-0762/DEL/PER/1000+/5/9148/2015 · 10,000+ verified placements since 2015</p>
        </div>
      </section>
    </div>
  );
}
